import React, { use, useEffect, useMemo, useRef, useState } from 'react';
import AvSchemaFieldCompletionEditor from '@components/AvSchemaFieldCompletionEditor';
import CodeEditor from '@components/CodeEditor';
import { AvButton, AvCheckbox, AvLabelWithHelptip, AvTooltip, TextInput } from '@fabric-ui/components';
import { Function, X } from '@fabric-ui/icons';
import { InfoFull } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { iconSize, noop } from '@fabric-ui/utils';
import { Box, ButtonProps, ClickAwayListener, IconButton, Paper, Popper, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import Select from '../../../components/Select';
import { useAvContext } from '../../../context/AvContextProvider';
import { DescriptorType, Usages } from '../../../context/context.type';
import useFullScreenToggle from '../../../hooks/useFullScreenToggle';
import { FeatureFlags } from '../../../types';
import CELExpressionInput from '../../DataUnification/CELExpressionInput';
import { getPythonType } from '../../ModelManagement/hooks';
import { useBridgeEntity } from './hooks/useBridgeEntity';
import { MappingContext } from './MappingContext';
import { MappingType } from './types';
import { formatExplodedFieldValue } from './utils';

const infoIcon = <InfoFull />;
const FIXES_TOOLTIP =
  'Fixes is a unique field, the returned value must be a list of dictionaries with the following structure: [{"fix": <FIX_STRING>, "type": <FIX_TYPE>}]. Supported fix types are “TEXT” and “VERSION”.';

const tooltipText = (footerText = 'Just remember, the value you return should match the type of the model field you are mapping to!') => (
  <div>
    <Box sx={{ fontWeight: 600 }}>A few notes to get you started</Box>
    <div>Rows of data stored as dictionaries. To access row values use square brackets or the get() method</div>
    <Box sx={{ color: theme => theme.palette.colors.neutrals[600], my: 3 }}>{`Example: row["MyDataField"] OR row.get("MyDataField")`}</Box>
    <div>{footerText}</div>
  </div>
);

const codeOverrides = {
  field: {
    fixes: value => `def evaluate(row: dict) -> list[dict]:
    item = row.get("${value}")\n    
    return [{
        "type": <USE "TEXT"/"VERSION">,
        "fix": item
    }]`,
  },
  type: {
    DATE: value => `from datetime import datetime\n
def evaluate(row: dict) -> str:
    date_string = row.get("${value}")
    ORIGINAL_DATE_FORMAT = '%Y-%m-%d' # Replace with source dateformat\n    
    return datetime.strptime(date_string, ORIGINAL_DATE_FORMAT).strftime('%Y-%m-%d')`,
    DATETIME: value => `from datetime import datetime\n
def evaluate(row: dict) -> str:
    date_string = row.get("${value}")
    ORIGINAL_DATE_FORMAT = '%Y-%m-%dT%H:%M:%S.%fZ' # Replace with source dateformat\n    
    return datetime.strptime(date_string, ORIGINAL_DATE_FORMAT).strftime('%Y-%m-%dT%H:%M:%SZ')`,
    IP: value => `import re

def validate_ip(ip_str: str):
    ip_pattern = re.compile(r'^(?:\\d{1,3}\\.){3}\\d{1,3}$')
    return ip_pattern.match(ip_str)

def evaluate(row: dict):
    input_data = row.get("${value}")
    ips = []
    
    SPLIT_CHARS = r',|\\|'
    
    if isinstance(input_data, str):
        input_data = input_data.strip()
        potential_ips = re.split(SPLIT_CHARS, input_data)
        for ip in potential_ips:
            ip = ip.strip()
            if validate_ip(ip):
                ips.append(ip)
                
    elif isinstance(input_data, list):
        for item in input_data:
            if isinstance(item, str) and validate_ip(item.strip()):
                ips.append(item.strip())

    return ips`,
  },
};

const getDefaultSignature = fieldType =>
  `def evaluate(row: dict) ${fieldType ? `-> ${Array.isArray(fieldType) ? 'str' : getPythonType(fieldType)}` : ''}:\n`;

const generateCodeAccordingToType = (fieldType, value) =>
  codeOverrides.type[fieldType]?.(value) ||
  (Array.isArray(fieldType) && [DescriptorType.TYPE_STRING, 'MESSAGE'].includes(fieldType[0])
    ? `${getDefaultSignature([DescriptorType.TYPE_STRING])}    item = row.get("${value}")\n
    if item:
        return [item]
    return []`
    : `${getDefaultSignature(fieldType)}    item = row.get("${value}")\n    return ${getPythonType(fieldType)}(item)`);

export const getInitialScript = (fieldType, value, type, modelFieldName) =>
  value
    ? type === MappingType.Path
      ? fieldType
        ? codeOverrides.field[modelFieldName]?.(value) || generateCodeAccordingToType(fieldType, value)
        : `${getDefaultSignature(fieldType)}    return row.get("${value}")`
      : `${getDefaultSignature(fieldType)}    return "${value}"`
    : `${getDefaultSignature(fieldType)}    return None`;

export const editorPopperTypeProps = {
  [MappingType.Script]: {
    name: 'Script',
    icon: <Function style={iconSize()} />,
    tooltip: modelFieldName => (
      <AvTooltip
        variant="message"
        color="white"
        muiProps={{ PopperProps: { sx: { '.MuiTooltip-tooltip': { maxWidth: 450 } } } }}
        title={
          <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
            <Box sx={{ alignSelf: 'start' }}>{infoIcon}</Box>
            <div>{tooltipText(modelFieldName === 'fixes' ? FIXES_TOOLTIP : undefined)}</div>
          </Box>
        }>
        <div>{infoIcon}</div>
      </AvTooltip>
    ),
  },
  [MappingType.StringTemplate]: {
    name: 'Smart Text',
    icon: null,
    tooltip: () => (
      <AvTooltip
        variant="message"
        color="white"
        muiProps={{ PopperProps: { sx: { '.MuiTooltip-tooltip': { maxWidth: 450 } } } }}
        title={
          <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
            <Box sx={{ alignSelf: 'start' }}>{infoIcon}</Box>
            <div>
              Define the field value using free text, or a combination of free text and selected fields. <br />
              <br /> To add fields, enclose them in double curly brackets. <strong>Example:</strong> {`{{Ticket Status}}`}
            </div>
          </Box>
        }>
        <div>{infoIcon}</div>
      </AvTooltip>
    ),
  },
  [MappingType.Literal]: { name: 'Value', icon: null },
  [MappingType.Path]: { name: 'Field', icon: null },
  [MappingType.Expression]: { name: 'Expression', icon: null },
  [MappingType.Composite]: {
    name: 'Composite',
    icon: null,
    title: 'Composite Value',
    tooltip: () => (
      <AvTooltip variant="message" color="white" title="Select multiple fields to combine their values into a single value.">
        <div>{infoIcon}</div>
      </AvTooltip>
    ),
  },
};

interface EditorPopperProps {
  open: boolean;
  value: string;
  buttons: (ButtonProps & { onActionClick(...args: unknown[]): unknown; isDisabled?: (value) => boolean })[];
  type: MappingType;
  fieldType?: string;
  setType(val: MappingType): void;
  handleClose(...args: unknown[]): unknown;
  anchorEl?: Element;
  onChange?(val: string): void;
  editable?: boolean;
  hideToggle?: boolean;
  modelFieldName?: string;
  language?: string;
  pythonScript?: boolean;
  projectionName?: string;
  FieldSelector?: any;
  ScriptEditor?: any;
  defaultHeight?: number | string;
  mappingTypes?: MappingType[];
  isMultiLineStringTemplate?: boolean;
  stringTemplateTitle?: string;
  stringTemplateIsInteractiveFields?: boolean;
  textInputAutoFocused?: boolean;
  exploded?: boolean;
  compositeValue?: string[];
  maximize?: boolean;
  codeEditorControls?: React.ReactNode;
  entityName?: string;
  replicaNumber?: number;
}

const EditorPopper = ({
  anchorEl,
  open,
  editable = true,
  buttons,
  value,
  onChange = noop,
  type,
  setType,
  fieldType,
  handleClose,
  modelFieldName,
  language,
  hideToggle,
  pythonScript = true,
  projectionName = '',
  FieldSelector,
  ScriptEditor,
  defaultHeight = '100%',
  mappingTypes = [MappingType.Script, MappingType.Literal, MappingType.Path],
  isMultiLineStringTemplate = false,
  stringTemplateIsInteractiveFields = false,
  textInputAutoFocused = false,
  exploded = false,
  compositeValue,
  maximize,
  codeEditorControls,
  entityName,
  replicaNumber,
}: EditorPopperProps) => {
  const theme = useTheme();
  const popperRef = useRef(null);
  const { fullScreenToggle, maximized } = useFullScreenToggle(popperRef.current);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const { isBridgeEntity } = useBridgeEntity();
  const { featureFlags } = useAvContext();

  useEffect(() => {
    if (maximize !== undefined && maximized !== maximize) {
      toggleButtonRef.current?.click();
    }
  }, [maximize, maximized]);

  const fullScreenToggleWithRef = fullScreenToggle && React.cloneElement(fullScreenToggle, { ref: toggleButtonRef });

  const values = useRef({
    [type]: value,
    [MappingType.Script]: (type === MappingType.Script && value) || getInitialScript(fieldType, value, type, modelFieldName),
    [MappingType.StringTemplate]: (type === MappingType.StringTemplate && value) || '',
  }).current;
  const onValueChange = newValue => {
    onChange(newValue);
    values[type] = newValue;
    if (type !== MappingType.Script && pythonScript) {
      values[MappingType.Script] = getInitialScript(fieldType, newValue, type, modelFieldName);
    }
  };

  const [isExploded, setIsExploded] = useState(exploded);

  const { mappings, fieldsList = [], hasExplodedField } = use(MappingContext);

  const [compositeFields, setCompositeFields] = useState(
    compositeValue ?? ((type === MappingType.Composite && value.split(', ').filter(Boolean)) || [])
  );

  const editPopperComponentByType = {
    [MappingType.Script]: ScriptEditor ? (
      React.cloneElement(ScriptEditor, { value: values[type], onChange: onValueChange })
    ) : (
      <Box
        sx={
          codeEditorControls
            ? { ...flex.col, height: '100%', border: `1px solid ${theme.palette.colors.neutrals[400]}`, borderRadius: '16px' }
            : { ...flex.col, height: '100%' }
        }>
        <CodeEditor
          value={values[type]}
          disabled={!editable}
          onChange={onValueChange}
          height={maximized ? '100%' : 300}
          language={language}
          {...(codeEditorControls && {
            style: {
              border: 'none',
              borderBottom: `1px solid ${theme.palette.colors.neutrals[400]}`,
              borderRadius: '16px 16px 0 0',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          })}
        />
        <Box sx={{ ...flex.justifyEndCenter, pr: 2, flex: 0 }}>{codeEditorControls}</Box>
      </Box>
    ),
    [MappingType.StringTemplate]: (
      <Box sx={{ position: 'relative' }}>
        <AvSchemaFieldCompletionEditor
          projectionName={projectionName}
          onChange={onValueChange}
          value={values[type]}
          height={isMultiLineStringTemplate ? 200 : undefined}
          isInteractive={stringTemplateIsInteractiveFields}
        />
      </Box>
    ),
    [MappingType.Path]: FieldSelector || (
      <TextInput
        value={values[type]}
        disabled={!editable}
        onChange={onValueChange}
        sx={{ maxWidth: 'unset' }}
        autoFocus={textInputAutoFocused}
      />
    ),
    [MappingType.Literal]: (
      <TextInput
        value={values[type]}
        disabled={!editable}
        onChange={onValueChange}
        sx={{ maxWidth: 'unset' }}
        autoFocus={textInputAutoFocused}
      />
    ),
    [MappingType.Expression]: (
      <Box sx={{ pt: 1 }}>
        <CELExpressionInput projectionName={projectionName} onChange={onValueChange} value={values[type]} usage={Usages.INTERACTIVE} />
      </Box>
    ),
    [MappingType.Composite]: (
      <Select
        placeholder="Select fields..."
        isMultiple
        muiProps={{ componentsProps: { paper: { sx: { maxWidth: '50px' } } } }}
        style={{
          maxWidth: '100%',
          '&.autocomplete-multiple': {
            '&, .MuiFormControl-root': {
              maxWidth: '100%',
            },
          },
        }}
        readOnly={!editable}
        size="medium"
        // @ts-ignore
        options={fieldsList.map(field => ({
          title: field.label,
          value: formatExplodedFieldValue(field.path, field.isExploded),
        }))}
        value={compositeFields}
        onChange={setCompositeFields}
      />
    ),
  };

  const enableExplode = useMemo(() => {
    if (exploded) {
      return true;
    }
    if (!entityName || !isBridgeEntity(entityName)) {
      return !hasExplodedField;
    }

    const hasExplodedInSameReplica = mappings.some(m => m.entityName === entityName && m.replicaNumber === replicaNumber && m.exploded);

    const hasExplodedInOtherReplicas = mappings.some(m => m.entityName === entityName && m.replicaNumber !== replicaNumber && m.exploded);

    return !hasExplodedInSameReplica && (hasExplodedInOtherReplicas || !hasExplodedField);
  }, [mappings, entityName, replicaNumber, hasExplodedField, exploded, isBridgeEntity]);

  return (
    <Popper
      ref={popperRef}
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      sx={{ width: 600, zIndex: theme => theme.zIndex.modal }}
      onClick={event => event.stopPropagation()}>
      <ClickAwayListener onClickAway={handleClose}>
        <Paper sx={{ p: 2, ...flex.col, gap: 2, height: defaultHeight || '100%' }}>
          <Box component="span" sx={{ ...flex.itemsCenter, color: theme => theme.palette.colors.neutrals[500], fontWeight: 600, gap: 1.5 }}>
            {editable ? (
              <>
                {!hideToggle ? (
                  <Box sx={{ ...flex.row, flexGrow: 1, gap: 1 }}>
                    {editorPopperTypeProps[type].icon}
                    {editorPopperTypeProps[type].title ?? `${editorPopperTypeProps[type].name} Editor`}
                    {editorPopperTypeProps[type].tooltip?.(modelFieldName)}
                  </Box>
                ) : (
                  <Box sx={{ flexGrow: 1 }} />
                )}
                {type && !hideToggle && (
                  <ToggleButtonGroup color="white" size="small" value={type} exclusive onChange={e => setType((e.target as any).value)}>
                    {Object.values(mappingTypes).map(mapType => (
                      <ToggleButton key={mapType} value={mapType} disabled={mapType === type}>
                        {editorPopperTypeProps[mapType].name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
                {fullScreenToggleWithRef}
              </>
            ) : (
              <>
                <Box sx={{ ...flex.row, flexGrow: 1, gap: 1 }}>
                  {editorPopperTypeProps[type].icon}
                  {editorPopperTypeProps[type].name}
                </Box>
                <IconButton onClick={handleClose}>
                  <X style={{ width: 20, height: 20 }} />
                </IconButton>
              </>
            )}
          </Box>
          {editPopperComponentByType[type]}
          {editable ? (
            <div style={{ ...flex.row, justifyContent: 'space-between', alignItems: 'center' }}>
              {featureFlags[FeatureFlags.ExplodeMapping] && type === MappingType.Script ? (
                <AvTooltip title={hasExplodedField || exploded ? 'Only one field can be exploded' : ' '} disabled={!enableExplode}>
                  <div>
                    <AvCheckbox
                      value={isExploded}
                      disabled={!enableExplode}
                      onChange={setIsExploded}
                      label={
                        <AvLabelWithHelptip
                          label="Explode Values"
                          message="Check this box to create a separate record for each item in the source list or array. If unchecked, the source will be mapped as a single record."
                          iconComponent={InfoFull}
                        />
                      }
                    />
                  </div>
                </AvTooltip>
              ) : (
                <div style={{ flex: 1 }} />
              )}
              <div style={{ ...flex.row, gap: 16 }}>
                {buttons.map(params => {
                  const { children, onActionClick, isDisabled, ...rest } = params;
                  return (
                    <AvButton
                      key={children as string}
                      onClick={() => {
                        onActionClick(type === MappingType.Composite ? compositeFields : values[type], type, isExploded);
                      }}
                      disabled={isDisabled?.(type === MappingType.Composite ? compositeFields : values[type])}
                      {...rest}>
                      {children}
                    </AvButton>
                  );
                })}
              </div>
            </div>
          ) : null}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default EditorPopper;

export const dropStyle = (selected, borderSide) => ({
  ...(selected && {
    div: {
      background: theme =>
        `linear-gradient(277.57deg, ${theme.palette.colors.primary[400]} -103.08%, ${theme.palette.colors.primary[200]} 155.47%)`,
      position: 'absolute',
      [borderSide]: '-2px',
      height: '110%',
      width: '7px',
      borderTopLeftRadius: '10px',
      borderBottomLeftRadius: '10px',
      transform: borderSide === 'left' && 'rotate(180deg)',
    },
  }),
});
